import type {
  CaptureAttention,
  CaptureConfidence,
  CaptureRecord,
  CaptureStatus,
  ConfirmCaptureRequest,
  CorrectionReason,
  FieldValue,
  LineageStage,
  QualityCard,
  ReasonCode,
  RerunTrigger,
  VoiceNote,
} from '@/lib/api/types';

/**
 * The pure half of the shared review surface (ADR-006): which attributes
 * render, in what order and under which tab, who sees which footer, and the
 * words each state is described in. Kept free of React so every rule the
 * screen applies is testable on its own.
 */

/** Statuses the pipeline is still moving the capture through. */
export const WORKING_STATUSES: CaptureStatus[] = ['RECEIVED', 'PREFLIGHT', 'EXTRACTING', 'VALIDATING'];

/** Statuses every review route answers 409 from (INTERFACES.md 15.2). */
export const FINISHED_STATUSES: CaptureStatus[] = ['APPROVED', 'AUTO_APPROVED', 'REJECTED', 'STAGED', 'SUBMITTED'];

/** Statuses `POST /capture/{id}/confirm` accepts. */
export const CONFIRMABLE_STATUSES: CaptureStatus[] = ['SCORED', 'IN_REVIEW', 'RECAPTURE_REQUESTED'];

/** Statuses `POST /capture/{id}/rerun` accepts. */
export const RERUNNABLE_STATUSES: CaptureStatus[] = ['SCORED', 'IN_REVIEW', 'RECAPTURE_REQUESTED'];

/** Statuses `POST /capture/{id}/frames` accepts, QUARANTINED among them. */
export const FRAME_CHANGE_STATUSES: CaptureStatus[] = ['SCORED', 'IN_REVIEW', 'RECAPTURE_REQUESTED', 'QUARANTINED'];

/** Statuses `POST /capture/{id}/escalate` accepts. */
export const ESCALATABLE_STATUSES: CaptureStatus[] = ['SCORED', 'IN_REVIEW'];

/** Three frames per capture, `MAX_ASSETS_PER_CAPTURE` on the API. */
export const MAX_FRAMES = 3;

/**
 * The reviewer footer goes to a reviewer looking at someone else's
 * capture (OPERATOR_ACCESS.md section 5). The person who captured it gets
 * the operator footer whatever their permissions, so a manager capturing
 * an item can still finish it at the shelf with "Done, next item".
 *
 * `reviewCaptures` is the caller's own resolved permission from
 * `GET /capture/me` (`lib/access/access-context.ts`), which replaces the
 * retired `custom:role` check: `apps/capture-api/src/review/reviewer-role.ts`
 * is gone, and the capture API now resolves the same right from the
 * member record (`apps/capture-api/src/access/resolver.ts`).
 */
export function showsReviewerFooter(
  reviewCaptures: boolean,
  viewerSub: string | null | undefined,
  capturedBy: string | null | undefined,
): boolean {
  if (viewerSub && capturedBy && viewerSub === capturedBy) return false;
  return reviewCaptures;
}

export function isWorking(record: CaptureRecord): boolean {
  return WORKING_STATUSES.includes(record.status);
}

export function hasTranscribingNote(record: CaptureRecord): boolean {
  return (record.voiceNotes ?? []).some((note) => note.status === 'TRANSCRIBING' || note.status === 'UPLOADED');
}

/**
 * Whether the screen should read the record again. A QUARANTINED capture
 * never changes on its own, so it stops the poll even with a voice note
 * still out for transcription.
 */
export function shouldPoll(record: CaptureRecord): boolean {
  if (record.status === 'QUARANTINED') return false;
  return isWorking(record) || hasTranscribingNote(record);
}

export type AttributeTab = 'identity' | 'descriptions';

export interface AttributeView {
  name: string;
  required: boolean;
  state: 'READ' | 'EMPTY' | 'VERIFIED';
  /** Absent for an EMPTY attribute. */
  field?: FieldValue;
  missingRequired: boolean;
  lowConfidence: boolean;
  tab: AttributeTab;
}

/*
  The same grouping the API's quality card uses (capture-api
  `quality/score-card.ts`), so an attribute scored under Descriptions is
  listed under the Descriptions tab.
*/
const IDENTITY_PATTERN = /manufactur|brand|model|part|mpn|sku|gtin|ean|upc|barcode|serial/i;
const DESCRIPTION_PATTERN = /descri|name|title|noun|modifier/i;

export function tabFor(name: string): AttributeTab {
  return !IDENTITY_PATTERN.test(name) && DESCRIPTION_PATTERN.test(name) ? 'descriptions' : 'identity';
}

function hasValue(field: FieldValue | undefined): field is FieldValue {
  return field !== undefined && field.value !== null && field.value !== '';
}

/**
 * Every attribute of the item standard, read or empty, with the required
 * ones still empty pinned to the top. Fields the record holds that the
 * standard does not name are kept at the end rather than hidden. An API
 * that returns no `attention` leaves the record's own fields as the list.
 */
export function buildAttributes(record: CaptureRecord, attention: CaptureAttention | undefined): AttributeView[] {
  const lowConfidence = new Set(attention?.lowConfidence ?? []);
  const missing = attention?.missingRequired ?? [];
  const views: AttributeView[] = [];
  const seen = new Set<string>();

  for (const attribute of attention?.attributes ?? []) {
    const field = record.fields[attribute.name];
    seen.add(attribute.name);
    views.push({
      name: attribute.name,
      required: attribute.required,
      state: hasValue(field) ? attribute.state : 'EMPTY',
      field: hasValue(field) ? field : undefined,
      missingRequired: missing.includes(attribute.name),
      lowConfidence: lowConfidence.has(attribute.name),
      tab: tabFor(attribute.name),
    });
  }

  for (const [name, field] of Object.entries(record.fields)) {
    if (seen.has(name)) continue;
    views.push({
      name,
      required: false,
      state: !hasValue(field) ? 'EMPTY' : field.verified ? 'VERIFIED' : 'READ',
      field: hasValue(field) ? field : undefined,
      missingRequired: false,
      lowConfidence: lowConfidence.has(name),
      tab: tabFor(name),
    });
  }

  const pinned = missing
    .map((name) => views.find((view) => view.name === name))
    .filter((view): view is AttributeView => view !== undefined);
  return [...pinned, ...views.filter((view) => !view.missingRequired)];
}

/** `model_number` as `Model number`. */
export function humanise(name: string): string {
  const spaced = name.replace(/[_-]+/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').trim().toLowerCase();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

/** The value as the edit box should start: never the string "null". */
export function editableValue(field: FieldValue | undefined): string {
  if (!field || field.value === null) return '';
  return String(field.value);
}

/**
 * Noun and modifier, the MDM naming the Base44 review leads with. A
 * standard that names neither falls back to the description the extraction
 * read, then to a plain placeholder.
 */
export function productHeadline(record: CaptureRecord): { title: string; fromNaming: boolean } {
  const noun = editableValue(record.fields.noun).trim();
  const modifier = editableValue(record.fields.modifier).trim();
  if (noun) return { title: modifier ? `${noun}, ${modifier}` : noun, fromNaming: true };
  const description = editableValue(record.fields.description).trim();
  if (description) return { title: description, fromNaming: false };
  return { title: 'Item not named yet', fromNaming: false };
}

export const STAGE_LABEL: Record<LineageStage, string> = {
  RAW_EVIDENCE: 'Raw evidence',
  DETERMINISTIC_READ: 'Read off the label',
  MODEL_EXTRACTION: 'AI read',
  CROSS_VERIFICATION: 'Cross-checked',
  TAXONOMY_VALIDATION: 'Taxonomy checked',
  QUALITY_GATE: 'Quality gated',
  VERIFIED: 'Verified by a person',
};

export const CORRECTION_REASONS: { value: CorrectionReason; label: string }[] = [
  { value: 'misread', label: 'Misread' },
  { value: 'wrong_field', label: 'Wrong field' },
  { value: 'missing', label: 'Was missing' },
  { value: 'unit', label: 'Unit' },
  { value: 'not_on_item', label: 'Not on the item' },
  { value: 'other', label: 'Other' },
];

/** Why a capture went to the manager, in words an operator uses. */
export const REASON_WORDS: Record<ReasonCode, string> = {
  MISSING_REQUIRED: 'required fields are still empty',
  VALIDATOR_FAILED: 'a value failed a validation check',
  QUALITY_GATE: 'a photograph was too poor to read reliably',
  LOW_SCORE: 'the reading scored below the approval threshold',
  NOT_CAPTURER: 'it was confirmed by someone other than the person who captured it',
  OPERATOR_ESCALATED: 'you asked your manager a question',
  PLACEHOLDER_CALIBRATION: 'scoring is not calibrated yet, so a manager checks every capture',
};

export const TRIGGER_LABEL: Record<RerunTrigger, string> = {
  RERUN: 'Rerun AI',
  RETAKE: 'Frame retaken',
  ADD_FRAME: 'Frame added',
};

export const NOTE_PURPOSE_LABEL: Record<VoiceNote['purpose'], string> = {
  NOTE: 'Note for the reviewer',
  CONTEXT: 'Hint for Rerun AI',
};

export const NOTE_FAILURE_WORDS: Record<NonNullable<VoiceNote['failureReason']>, string> = {
  PROVIDER_UNAVAILABLE: 'The transcription service was unavailable.',
  UNSUPPORTED_AUDIO: 'The recording format could not be read.',
  EMPTY_AUDIO: 'The recording was silent.',
  UNKNOWN: 'The recording could not be transcribed.',
};

export function formatDuration(ms: number): string {
  const seconds = Math.max(0, Math.round(ms / 1000));
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * A draft value as the API should receive it. A field the machine read as a
 * number goes back as a number when the operator typed one, so a corrected
 * bore of 25 does not turn into the string "25".
 */
function coerce(draft: string, original: FieldValue | undefined): string | number | boolean | null {
  const trimmed = draft.trim();
  if (trimmed === '') return null;
  if (typeof original?.value === 'number' && Number.isFinite(Number(trimmed))) return Number(trimmed);
  if (typeof original?.value === 'boolean' && (trimmed === 'true' || trimmed === 'false')) return trimmed === 'true';
  return trimmed;
}

/**
 * The confirm body from the screen's drafts. A draft on a field that holds a
 * value is an edit, sent only when it differs from what the machine read. A
 * draft on an empty attribute is an add, sent only when it holds something.
 * Reasons travel for the fields sent and no others.
 */
export function buildConfirmRequest(
  attributes: AttributeView[],
  drafts: Record<string, string>,
  reasons: Record<string, CorrectionReason>,
): ConfirmCaptureRequest {
  const fieldEdits: NonNullable<ConfirmCaptureRequest['fieldEdits']> = {};
  const fieldAdds: NonNullable<ConfirmCaptureRequest['fieldAdds']> = {};
  const byName = new Map(attributes.map((attribute) => [attribute.name, attribute]));

  for (const [name, draft] of Object.entries(drafts)) {
    const attribute = byName.get(name);
    if (attribute?.field) {
      if (draft !== editableValue(attribute.field)) fieldEdits[name] = coerce(draft, attribute.field);
    } else if (draft.trim() !== '') {
      fieldAdds[name] = coerce(draft, undefined);
    }
  }

  const sent = new Set([...Object.keys(fieldEdits), ...Object.keys(fieldAdds)]);
  const sentReasons = Object.fromEntries(Object.entries(reasons).filter(([name]) => sent.has(name)));

  return {
    ...(Object.keys(fieldEdits).length > 0 ? { fieldEdits } : {}),
    ...(Object.keys(fieldAdds).length > 0 ? { fieldAdds } : {}),
    ...(Object.keys(sentReasons).length > 0 ? { reasons: sentReasons } : {}),
  };
}

/** Whether any draft would change what is sent. */
export function hasPendingChanges(request: ConfirmCaptureRequest): boolean {
  return Boolean(request.fieldEdits || request.fieldAdds);
}

/**
 * The capture's status in the operator's words. The API's codes (IN_REVIEW,
 * SCORED) never reach the screen.
 */
const STATUS_LABEL: Record<CaptureStatus, string> = {
  QUEUED: 'Waiting to send',
  RECEIVED: 'Received',
  PREFLIGHT: 'Checking',
  EXTRACTING: 'Reading',
  VALIDATING: 'Checking values',
  SCORED: 'Ready for you to check',
  AUTO_APPROVED: 'Approved automatically',
  IN_REVIEW: 'Waiting for the manager',
  APPROVED: 'Approved',
  REJECTED: 'Rejected',
  RECAPTURE_REQUESTED: 'Retake requested',
  STAGED: 'In a classification batch',
  SUBMITTED: 'Sent for classification',
  QUARANTINED: 'Could not be read',
  REMOVED: 'Removed by the manager',
};

export function statusLabel(status: CaptureStatus): string {
  return STATUS_LABEL[status] ?? status;
}

/**
 * The grade chip's words on the result screen: grade and overall score, or
 * the fused confidence alone from an older API that returns no card. Null
 * when there is neither, and the chip does not render.
 */
export function qualityChipLabel(card?: QualityCard, confidence?: CaptureConfidence): string | null {
  if (card) return `${card.grade} ${card.overall}%`;
  if (confidence) return `${Math.round(confidence.fused * 100)}%`;
  return null;
}

/**
 * The labels of the required fields that came back empty, for the line
 * beside the frame strip (INTERFACES.md section 5): names from
 * `attention.missingRequired`, in the order of `attention.attributes`.
 */
export function missingRequiredLabels(attention: CaptureAttention | undefined): string[] {
  if (!attention) return [];
  const missing = new Set(attention.missingRequired);
  const ordered = attention.attributes.filter((attribute) => missing.has(attribute.name)).map((a) => a.name);
  const rest = attention.missingRequired.filter((name) => !ordered.includes(name));
  return [...ordered, ...rest].map((name) => humanise(name));
}
